import { ActionCreator } from 'redux';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import SearchHelper from '../../search/SearchHelper';

const actionTypes = {
  CHANGE_CATEGORY_FILTER: 'categoryFilter/CHANGE_CATEGORY_FILTER',
  FETCH_SEARCH_SUGGESTION: 'searchSuggestion/FETCH_SEARCH_SUGGESTION'
} as const;

export interface ISearchConditionsState {
  query: string;
  // 初期表示時点のカテゴリ選択状況
  initialCategories: ICategoryFilterState;
  // 初期表示後のユーザー操作で変わる最新のカテゴリ選択状況
  latestCategories: ICategoryFilterState;
  // 検索条件で選択されたカテゴリのカンマ区切りの一覧
  rawCategories?: string;
  // サジェスト一覧
  suggestList: string[];
  // query rewrite
  qrw: boolean;
  // 検索トップ判定
  isTopPage: boolean;
}

interface IYspaAPI {
  apiBaseUrl: string;
  appId?: string;
}

// webpackのdefine pluginで定義
// eslint-disable-next-line no-underscore-dangle
declare const _YSPA_: IYspaAPI;

export interface ICategoryFilterState {
  domestic: boolean;
  world: boolean;
  business: boolean;
  entertainment: boolean;
  sports: boolean;
  it: boolean;
  science: boolean;
  life: boolean;
  local: boolean;
}

const initialState: ISearchConditionsState = {
  query: '',
  initialCategories: {
    domestic: false,
    world: false,
    business: false,
    entertainment: false,
    sports: false,
    it: false,
    science: false,
    life: false,
    local: false
  },
  latestCategories: {
    domestic: false,
    world: false,
    business: false,
    entertainment: false,
    sports: false,
    it: false,
    science: false,
    life: false,
    local: false
  },
  suggestList: [],
  qrw: true,
  isTopPage: false
};

const changeCategoryFilter = (
  categoryFilter:
    | { domestic: boolean }
    | { world: boolean }
    | { business: boolean }
    | { entertainment: boolean }
    | { sports: boolean }
    | { it: boolean }
    | { science: boolean }
    | { life: boolean }
    | { local: boolean }
) => {
  return {
    type: actionTypes.CHANGE_CATEGORY_FILTER,
    payload: {
      categoryFilter
    }
  };
};

const fetchSearchSuggestionSuccess = (word: string, suggestList: ReadonlyArray<string>) => ({
  type: actionTypes.FETCH_SEARCH_SUGGESTION,
  payload: {
    suggestList,
    word
  }
});

export type Action = ReturnType<typeof changeCategoryFilter | typeof fetchSearchSuggestionSuccess>;

const fetchSearchSuggestion: ActionCreator<ThunkAction<Promise<void>, ISearchConditionsState, void, Action>> = (
  word: string
) => {
  return async (dispatch: ThunkDispatch<ISearchConditionsState, void, Action>) => {
    const commonUrl = `${_YSPA_.apiBaseUrl}?query=${encodeURIComponent(word)}&.src=web_news`;
    const url = _YSPA_.appId !== undefined ? `${commonUrl}&appid=${_YSPA_.appId}` : `${commonUrl}`;
    try {
      return fetch(url, {
        method: 'GET',
        credentials: 'same-origin'
      })
        .then(res => {
          if (res.ok) {
            return res.json();
          }
          const statusCode = res.status;
          return Promise.reject({ statusCode });
        })
        .then(body => {
          const suggestList = body.Result.map((suggestion: { Suggest: string }) => {
            return suggestion.Suggest;
          });
          dispatch(fetchSearchSuggestionSuccess(word, suggestList));
        });
    } catch {
      return;
    }
  };
};

export const actions = {
  changeCategoryFilter,
  fetchSearchSuggestion
} as const;

export default (state: ISearchConditionsState = initialState, action: Action) => {
  switch (action.type) {
    case actionTypes.CHANGE_CATEGORY_FILTER:
      return {
        ...state,
        latestCategories: Object.assign(state.latestCategories, action.payload.categoryFilter)
      };
    case actionTypes.FETCH_SEARCH_SUGGESTION: {
      // 検索語の単語数を取る
      const queryWordCount = SearchHelper.normalizeWord(action.payload.word).split(/\s/).length;
      return {
        ...state,
        // 検索ワードの単語数以下のもののみ格納する
        suggestList: action.payload.suggestList.filter(
          (suggestWord: string) => suggestWord.split(/\s/).length <= queryWordCount
        )
      };
    }
    default:
      return state;
  }
};
